import {defineStore} from 'pinia'
import {useGetPartnerPortalData} from '@/uses/Partner/useGetPartnerPortalData'
import {computed, watchEffect} from 'vue'
import useMeta from 'quasar/src/composables/use-meta.js';
import {useEnvStore} from '@/stores/useEnvStore'

// При изменении синхронизировать с $not-found-bg в src/views/NotFoundView.vue
const NOT_FOUND_BG = '#000000'

export const usePartnerStore = defineStore(
  'partner',
  () => {
    const {
      loading,
      data,
      status,
      load,
    } = useGetPartnerPortalData()

    const loaded = computed(() => {
      /*if (window.location.hostname.includes('sign.gosoblako.com')
        //|| window.location.hostname.includes('sign.localfenixfin.ru')
      ) {
        return true
      }*/

      return !!data.value && status.value !== 404
    })

    const enabledStyles = computed(() => {
      if (!data.value) {
        return false
      }

      return loaded.value && data.value.enabled
    })

    const agentCode = computed(() => {
      if (!data.value) {
        return undefined
      }

      return data.value.agent_code
    })

    const styles = computed(() => {
      if (!data.value || !data.value.enabled) {
        return {
          '--btn_text_color': 'white',
          '--btn_background_color': '--q-primary',
          '--input_text_color': 'rgba(0, 0, 0, 0.6)',
          '--input_text_border': 'rgba(0, 0, 0, 0.6)',
          '--input_background_color': '#eceff1',
          '--home_page_background_color': '#263238',
          '--other_page_background_color': '#263238',
          '--home_page_background_dark_color': '#263238',
          '--logo_placeholder_color': 'white',
          '--home_page_background_image': 'url(/main.jpg)',
          '--other_page_background_image': 'url(/main.jpg)',
        }
      }

      const inputTextColor = ensureContrast(
        data.value.design_config.input_background_color,
        data.value.design_config.input_text_color,
      )

      return {
        '--btn_text_color': data.value.design_config.btn_text_color || 'white',
        '--btn_background_color': data.value.design_config.btn_background_color || '#027BE3',
        '--input_text_color': inputTextColor,
        '--input_text_border': hexToRgba(data.value.design_config.input_text_color) || 'rgba(0, 0, 0, 0.3)',
        '--input_background_color': data.value.design_config.input_background_color || '#eceff1',
        '--home_page_background_color': data.value.design_config.home_page_background_color || '#263238',
        '--home_page_background_dark_color': newShade(data.value.design_config.home_page_background_color, -10) || '#263238',
        '--other_page_background_color': data.value.design_config.other_page_background_color || '#263238',
        '--logo_placeholder_color': data.value.design_config.logo_placeholder_color || 'white',
        '--home_page_background_image': `url(${data.value.design_config.home_page_background_image || '/main.jpg'})`,
        '--other_page_background_image': `url(${data.value.design_config.other_page_background_image || '/main.jpg'})`,
        '--banner_btn_background_color': data.value?.banner.btn_background_color || '#027BE3',
        '--banner_btn_text_color': data.value?.banner.btn_text_color || 'white',
        '--banner_background_color': data.value?.banner.background_color || '#f6c1c1',
      }
    })

    const logoUrl = computed(() => {
      if (!data.value || !data.value.enabled || !data.value.design_config.logo) {
        return '/main_logo.svg'
      }

      return data.value.design_config.logo
    })

    const logoInnerUrl = computed(() => {
      if (!data.value || !data.value.enabled || !data.value.design_config.logo) {
        return '/logo.svg'
      }

      return data.value.design_config.logo
    })

    const hasLogoImage = computed(() => {
      if (!data.value || !data.value.enabled) {
        return true
      }

      return !!data.value.design_config.logo
    })

    const phone = computed(() => {
      if (enabledStyles.value && data.value) {
        return data.value.design_config.support_phone
      }

      return '+7 (499) 110 16 42'
    })

    const email = computed(() => {
      if (enabledStyles.value && data.value) {
        return data.value.design_config.support_email
      }

      return 'support@gosoblako.com'
    })

    const setMeta = () => {
      let link = document.querySelector<HTMLLinkElement>('link[rel~=\'icon\']')
      if (!link) {
        link = document.createElement('link')
        document.head.appendChild(link)
        link.rel = 'icon'
      }
      if (status.value === 404) {
        link.href = '/empty-favicon.ico'
        useMeta(() => {
          return {
            title: 'Ошибка 404'
          }
        })

        return
      }
      if (data.value && data.value.enabled) {
        link.href = data.value.design_config.favicon || '/default-partner-icon.ico'
        useMeta(() => {
          return {
            title: 'Подписание КЭП'
          }
        })

        return
      }

      link.href = '/favicon.ico'
      useMeta(() => {
        return {
          title: 'GOSOBLAKO'
        }
      })
    }

    const hexToRgba = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

      return result
        ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, 0.2)`
        : undefined
    }

    const newShade = (hexColor: string, magnitude: number) => {
      hexColor = (hexColor || '').replace(`#`, ``)
      if (hexColor.length === 6) {
        const decimalColor = parseInt(hexColor, 16)
        let r = (decimalColor >> 16) + magnitude
        r > 255 && (r = 255)
        r < 0 && (r = 0)
        let g = (decimalColor & 0x0000ff) + magnitude
        g > 255 && (g = 255)
        g < 0 && (g = 0)
        let b = ((decimalColor >> 8) & 0x00ff) + magnitude
        b > 255 && (b = 255)
        b < 0 && (b = 0)

        return `#${(g | (b << 8) | (r << 16)).toString(16)}`
      } else {
        return hexColor
      }
    }

    /**
     * Переводит HEX в относительную яркость (luminance) по стандарту WCAG
     */
    const getLuminance = (hex: string) => {
      const rgb = hexToRgb(hex);
      const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(v => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }

    /**
     * Парсит HEX в { r, g, b }
     */
    const hexToRgb = (hex: string) => {
      const clean = hex.replace('#', '');
      const full = clean.length === 3
        ? clean.split('').map(c => c + c).join('')
        : clean;
      return {
        r: parseInt(full.slice(0, 2), 16),
        g: parseInt(full.slice(2, 4), 16),
        b: parseInt(full.slice(4, 6), 16),
      };
    }

    /**
     * Вычисляет коэффициент контрастности по WCAG (от 1 до 21)
     */
    const getContrastRatio = (hex1: string, hex2: string) => {
      const l1 = getLuminance(hex1);
      const l2 = getLuminance(hex2);
      const lighter = Math.max(l1, l2);
      const darker  = Math.min(l1, l2);
      return (lighter + 0.05) / (darker + 0.05);
    }

    /**
     * Если цвет 2 недостаточно контрастен на фоне цвета 1 —
     * заменяет его на чёрный или белый (смотря что контрастнее).
     *
     * @param {string} bgColor   — цвет фона,    например '#ffffff'
     * @param {string} textColor — цвет текста,  например '#000000'
     * @param {number} threshold — минимальный допустимый контраст (по WCAG AA = 4.5)
     * @returns {string} — исходный или скорректированный цвет текста
     */
    const ensureContrast = (bgColor: string, textColor: string, threshold = 4.5) => {
      const ratio = getContrastRatio(bgColor, textColor);

      if (ratio >= threshold) {
        return textColor; // контраст достаточный — оставляем
      }

      // Выбираем чёрный или белый — что даст больший контраст
      const withBlack = getContrastRatio(bgColor, '#424242');
      const withWhite = getContrastRatio(bgColor, '#f0f0f0');

      return withBlack >= withWhite ? '#000000' : '#ffffff';
    }

    watchEffect(() => {
      if (status.value === 404) {
        Object.keys(styles.value)
          .forEach((name) => {
            document.documentElement.style.removeProperty(name)
          })
        document.documentElement.style.backgroundColor = NOT_FOUND_BG
        document.body.style.backgroundColor = NOT_FOUND_BG
        document.body.classList.remove('partnerPortalMode')
        document.body.classList.remove('btn_text_color')
        document.body.classList.remove('btn_background_color')
        document.body.classList.remove('input_text_color')
        document.body.classList.remove('input_text_border')
        document.body.classList.remove('input_background_color')
        document.body.classList.remove('other_page_background_color')
        document.body.classList.remove('home_page_background_color')
        document.body.classList.remove('logo_placeholder_color')
        document.body.classList.remove('other_page_background_image')
        document.body.classList.remove('home_page_background_image')
        setMeta()

        return
      }

      document.documentElement.style.removeProperty('background-color')
      document.body.style.removeProperty('background-color')

      Object.entries(styles.value)
        .forEach(([name, value]) => {
          document.documentElement.style.setProperty(name, value)
        })
      document.body.classList.remove('partnerPortalMode')
      document.body.classList.remove('btn_text_color')
      document.body.classList.remove('btn_background_color')
      document.body.classList.remove('input_text_color')
      document.body.classList.remove('input_text_border')
      document.body.classList.remove('input_background_color')
      document.body.classList.remove('other_page_background_color')
      document.body.classList.remove('home_page_background_color')
      document.body.classList.remove('logo_placeholder_color')
      document.body.classList.remove('other_page_background_image')
      document.body.classList.remove('home_page_background_image')

      if (status.value) {
        setMeta()
      }

      if (data.value && !enabledStyles.value && data.value.agent_code) {
        const envStore = useEnvStore()
        if (window.location.hostname.includes('sign.gosoblako.com')) {
          return
        }
        window.location.href = `https://${data.value.agent_code}${envStore.editable ? '.pre' : ''}.sign.gosoblako.com/`

        return
      }

      if (!enabledStyles.value || !data.value) {
        return
      }

      document.body.classList.add('partnerPortalMode')
      //const color = data.value.design_config.home_page_background_color || '#000606'
      //const image = data.value.design_config.home_page_background_image || '/main.jpg'
      //document.documentElement.style.backgroundColor = `${color}`
      //document.documentElement.style.backgroundImage = `url('${image}')`

      if (data.value.design_config.btn_text_color) {
        document.body.classList.add('btn_text_color')
      }
      if (data.value.design_config.btn_background_color) {
        document.body.classList.add('btn_background_color')
      }
      if (data.value.design_config.input_text_color) {
        document.body.classList.add('input_text_color')
      }
      if (data.value.design_config.input_text_color) {
        document.body.classList.add('input_text_border')
      }
      if (data.value.design_config.input_background_color) {
        document.body.classList.add('input_background_color')
      }
      if (data.value.design_config.other_page_background_color) {
        document.body.classList.add('other_page_background_color')
      }
      if (data.value.design_config.home_page_background_color) {
        document.body.classList.add('home_page_background_color')
      }
      if (data.value.design_config.logo_placeholder_color) {
        document.body.classList.add('logo_placeholder_color')
      }
      if (data.value.design_config.other_page_background_image) {
        document.body.classList.add('other_page_background_image')
      }
      if (data.value.design_config.home_page_background_image) {
        document.body.classList.add('home_page_background_image')
      }
    })

    return {
      loading,
      loaded,
      styles,
      logoUrl,
      logoInnerUrl,
      hasLogoImage,
      data,
      enabledStyles,
      agentCode,
      phone,
      email,
      status,
      load,
    }
  })
